// Customer-facing strings for the Content Prompt Builder pages and island, verbatim from
// docs/prompt-builder/copy-deck.md. Change the deck first, then this file.

import type { Category } from "./templates";

export const HUB_COPY = {
  titleTag: "Free AI prompt builder for social media | Sked Social",
  metaDescription:
    "Free AI prompts for social media, filled in for your brand. Pick a goal and a platform, paste the prompt into ChatGPT or Gemini, then get all 50 for your brand.",
  eyebrow: "Free tool from Sked Social",
  h1Before: "AI prompts for social media, filled in for ",
  h1Highlight: "your",
  h1After: " brand",
  subhead:
    "Pick a goal and a platform, add your brand, then paste the prompt into ChatGPT, Gemini or any AI tool.",
  primaryCta: "Build my prompt",
  underCta: 'Based on Gabby Torres-Soler\'s guide, "Steal this: 50 AI content prompts".',
  howHeading: "How it works",
  howSteps: [
    {
      lead: "Pick a goal and a platform.",
      body: "Brand story, authority, engagement, conversion or short-form video, for Instagram, LinkedIn, TikTok, Facebook or YouTube Shorts.",
    },
    {
      lead: "Tell it about your brand.",
      body: "Your brand name, who you're talking to and what kind of business you run. Add your brand voice and content pillars if you like.",
    },
    {
      lead: "Copy, paste, post.",
      body: "Paste the prompt into your AI tool and edit what comes back. Want all 50 filled in for your brand? Add your email and we'll send them over.",
    },
  ],
  libraryHeading: "50 prompts, five goals",
  libraryIntro: "Every prompt does one job. Pick the goal that matters this week.",
  seeAll: "See all 10",
} as const;

export const FAQ_HEADING = "FAQ";

export const FAQ: ReadonlyArray<{ question: string; answer: string }> = [
  {
    question: "Is it free?",
    answer:
      "Yes. Build and copy as many prompts as you like, no email needed. Your pack of 50 prompts, 3 AI post ideas and shareable boards are free too. We ask for an email so you can come back to them.",
  },
  {
    question: "Which AI tools do the prompts work with?",
    answer:
      "ChatGPT, Gemini, Claude and most other AI writing tools. The prompts are plain text, so they work anywhere you can paste.",
  },
  {
    question: "What's in the pack?",
    answer:
      "All 50 prompts across five goals, filled in with your brand, audience and platform. You also get 3 post ideas written by AI from your first prompt. It comes as a private web page with copy buttons, plus a PDF to keep.",
  },
  {
    question: "Why do you want a work email?",
    answer:
      "Your pack and a few short follow-ups on using it go to that address, so it helps if it's the one you plan content from. A personal address works too.",
  },
  {
    question: "Where do the prompts come from?",
    answer:
      'From Gabby Torres-Soler\'s guide, "Steal this: 50 AI content prompts", on the Sked Social blog. We rewrote each one so it fills in for your business, whether you run an agency, a venue or a creator account.',
  },
  {
    question: "Will AI write my posts for me?",
    answer:
      "It writes a first draft. Each prompt sets the audience, format and tone, and prompts that need real numbers or quotes leave [brackets] for you to fill in. Read and fact-check before you post.",
  },
  {
    question: "What happens to my details?",
    answer:
      "We keep your inputs so your pack and boards keep working. Packs, profiles and boards are deleted after 90 days without a visit. Boards you publish are public, so only add what you're happy to share.",
  },
  {
    question: "Can I use these prompts in Sked Social?",
    answer:
      "Yes. Save the best results in Sked Ideas, sort them by content pillar, and turn the keepers into scheduled posts in one click. Try Sked Social free for 14 days, no credit card needed.",
  },
];

export const CATEGORY_PAGE_COPY: Record<Category, { titleTag: string; metaDescription: string }> = {
  "brand-story": {
    titleTag: "10 brand story AI prompts for social media | Sked Social",
    metaDescription:
      "Copy-paste AI prompts for origin stories, customer heroes and behind-the-scenes posts. Filled in for your brand, ready for ChatGPT or Gemini. Free.",
  },
  authority: {
    titleTag: "10 AI prompts for thought leadership posts | Sked Social",
    metaDescription:
      "AI prompts for expert plans, frameworks, myth-busting and lessons learned. Show what you know without a week of writing. Filled in for your brand, free.",
  },
  engagement: {
    titleTag: "10 AI prompts that get more comments | Sked Social",
    metaDescription:
      "Polls, debates, fill-in-the-blanks and this-or-that prompts for ChatGPT or Gemini. Built to start conversations, not collect likes. Filled in for your brand.",
  },
  conversion: {
    titleTag: "10 AI sales post prompts that aren't salesy | Sked Social",
    metaDescription:
      "AI prompts for problem-first captions, case studies, objection busters and launches. Make your product the obvious next step. Filled in for your brand, free.",
  },
  "short-video": {
    titleTag: "10 AI prompts for Reels, TikTok and Shorts | Sked Social",
    metaDescription:
      "AI prompts for hooks, 60-second scripts, POV skits and shot lists. Get from blank screen to filming today. Filled in for your brand and platform, free.",
  },
};

export const CATEGORY_COPY = {
  exampleNotice:
    "These are filled in for a made-up coffee brand. Build yours above to swap in your own details.",
  copyPrompt: "Copy prompt",
  usePrompt: "Use this prompt",
  bottomCtaHeading: "Make these prompts yours",
  bottomCtaBody: "Add your brand and audience and every prompt fills itself in.",
  bottomCtaButton: "Build my prompt",
  otherGoalsHeading: "More prompts by goal",
} as const;

export const GOAL_HELPERS: Record<Category, string> = {
  "brand-story": "Show the people and decisions behind your brand.",
  authority: "Prove you know your stuff.",
  engagement: "Get people talking in the comments.",
  conversion: "Turn interest into enquiries and sales.",
  "short-video": "Hooks and scripts for Reels, TikTok and Shorts.",
};

export const BUILDER_COPY = {
  heading: "Build your prompt",
  goalLabel: "What's this post for?",
  platformLabel: "Where will it go?",
  generate: "Generate my prompt",
  businessTypeLabel: "What kind of business?",
  businessTypeHelper: "Some prompts change to fit your business.",
  toneLabel: "Brand voice (optional)",
  tonePlaceholder: "e.g. warm, direct, a little cheeky",
  toneHelper: "Three or four words is plenty.",
  pillarsLabel: "Content pillars (optional)",
  pillarsPlaceholder: "e.g. brewing tips, our growers, new releases",
  pillarsHelper: "The themes you post about most.",
  pickerLabel: "Pick a prompt",
  inlineHint: "Click a [bracket] like this one to fill it in.",
  platformMismatch:
    "This one works best on {platforms}. It'll still work on {platform} with a few edits.",
  personalise: "Personalise",
  variantHint: "This prompt changes to fit your business.",
  variantAction: "Choose your business type",
  resultHeading: "Your prompt",
  copy: "Copy prompt",
  copied: "Copied",
  copyFailed: "Couldn't copy. Select the text and copy it instead.",
  resultHelper:
    "Paste it into ChatGPT, Gemini or your AI tool of choice. Anything in [brackets] is yours to fill in.",
  tryAnother: "Try another prompt",
  nextHeadingWithBrand: "Get all 50 for {brand}",
  nextHeading: "Get all 50 for your brand",
  nextBody:
    "Every prompt filled in for {audience} on {platform}, plus 3 post ideas written for you. Free.",
  nextButton: "Send me all 50",
} as const;

export const GATE_COPY = {
  headline: "Get all 50 prompts for {brand}",
  sub: "We'll email you a private link. Click it and your pack opens straight away, and it's how you come back to it later.",
  bullets: [
    "All 50 prompts, filled in for your brand, audience and platform",
    "3 post ideas written by AI from your first prompt",
    "A PDF to keep, and boards you can share with your team or clients",
  ],
  emailLabel: "Work email",
  emailPlaceholder: "you@company.com",
  roleLabel: "Your role",
  rolePlaceholder: "Choose one",
  roles: [
    "Social media manager",
    "Marketing manager",
    "Head of marketing or CMO",
    "Agency owner or account lead",
    "Founder or business owner",
    "Content creator",
    "Something else",
  ],
  businessTypeLabel: "What kind of business?",
  accountsLabel: "How many social accounts do you look after?",
  accountsHelper: "Count every brand, client or location.",
  accounts: ["1 to 3", "4 to 10", "11 to 25", "26 to 50", "More than 50"],
  newsletterLabel: "Get occasional updates from Sked Social about social media best practices",
  newsletterHelper: "Optional. Uncheck to skip, or unsubscribe at any time.",
  emailRequired: "Email is required.",
  emailInvalid: "That email doesn't look right. Check for typos.",
  freemailNudge:
    "Use your work email if you can, so your pack and tips land with the rest of your work. A personal address still works.",
  emailUndeliverable: "We can't find that email domain. Check for typos.",
  emailDisposable: "We can't send your pack to a temporary inbox. Use your work email instead.",
  roleRequired: "Choose your role.",
  businessTypeRequired: "Choose your business type.",
  accountsRequired: "Choose a number.",
  button: "Email me all 50",
  loading: "Sending your link...",
  turnstileFailed: "We couldn't confirm you're human. Refresh the page and try again.",
  rateLimited: "Too many tries from this connection. Wait a few minutes, then try again.",
  emailRateLimited:
    "We've sent several links to this address just now. Wait a minute, then try again.",
  serverError: "Something went wrong on our side. Your details are safe. Try again in a minute.",
  privacyLine:
    "We'll email your pack link now, then up to 3 short follow-ups over the next 9 days. Unsubscribe any time.",
  privacyLink: "Privacy policy",
  close: "Not now",
} as const;

export const IDEAS_CTA_COPY = {
  eyebrow: "When the ideas pile up",
  heading: "Keep every idea in one place",
  body: "Sked Ideas holds every idea in one pool, sorted by content pillar, so you can see if your plan leans on one theme. When an idea's ready, one click turns it into a scheduled post with its caption and assets attached.",
  button: "Try Sked Social free",
  underButton: "14-day free trial. No credit card needed.",
  secondaryLink: "See how Sked Ideas works",
} as const;

export const PACK_COPY = {
  eyebrow: "Your prompt pack",
  h1: "50 AI prompts for {brand}",
  sub: "Filled in for {audience} on {platform}. Copy a prompt, paste it into your AI tool and fill in anything in [brackets].",
  downloadPdf: "Download PDF",
  pdfRendering: "Your PDF is nearly ready. Try again in a minute.",
  pdfFailed: "We couldn't make your PDF. Refresh the page to try again.",
  pdfReady: "Your PDF is ready.",
  pdfReadyLink: "Download your PDF",
  copy: "Copy",
  copied: "Copied",
  ideasHeading: "3 post ideas for {brand}",
  ideasSub: 'Written by AI from your "{prompt}" prompt. Edit before you post.',
  ideaHookLabel: "Hook",
  copyIdea: "Copy idea",
  ideasPending: "Your 3 post ideas are on their way. Refresh the page in a moment.",
  privacyFooter:
    "This page is private to you, but anyone with the link can open it, so keep it to yourself.",
} as const;

/** An unconfirmed link: one click confirms. GET never confirms, because mail scanners
 *  fetch every link on delivery. */
export const CONFIRM_PAGE_COPY = {
  heading: "One click to open your 50 prompts",
  body: "Your pack for {brand} is ready. Opening it confirms this is your email address.",
  button: "Open my prompts",
} as const;

/** Double opt-in: the gate after a successful submit. */
export const GATE_SENT_COPY = {
  headline: "Check your inbox",
  body: "We've sent a link to {email}. Open it to see all 50 prompts for {brand}.",
  help: "It can take a couple of minutes to arrive. If you can't see it, check your spam or promotions folder.",
  edit: "Wrong address? Edit it",
  resend: "Send the link again",
  resendWait: "You can send it again in {seconds} seconds.",
  resent: "We've sent another link to {email}.",
} as const;

/** An unknown, expired or deleted link. */
export const LINK_PROBLEM_COPY = {
  heading: "This link doesn't work",
  body: "The pack may have been deleted, or part of the link went missing when it was copied. Build your prompts again and we'll email you a fresh link.",
  button: "Build my prompt",
} as const;

export const DELETE_COPY = {
  link: "Delete my data",
  heading: "Delete your pack?",
  body: "This removes {brand}'s pack, your saved answers, your links and your PDF from this site. This can't be undone. To stop emails from Sked Social, use the unsubscribe link at the bottom of any of them.",
  keep: "Keep my pack",
  confirm: "Delete everything",
  doneHeading: "Your pack is deleted",
  doneBody:
    "We've removed your pack, your saved answers, your links and your PDF from this site. To stop emails from Sked Social, use the unsubscribe link at the bottom of any of them.",
  doneButton: "Build my prompt",
} as const;

/** The double opt-in email, sent on every capture. */
export const CONFIRM_EMAIL_COPY = {
  fromName: "Sked Social",
  subject: "Confirm your email to open your 50 prompts",
  preheader: "One click and your prompt pack for {brand} opens.",
  heading: "Your 50 prompts for {brand} are ready",
  body: "Confirm this is your email address and your pack opens straight away. All 50 prompts are filled in for {audience} on {platform}.",
  button: "Confirm and open my pack",
  fallback: "Button not working? Paste this link into your browser:",
  privacy: "The link is private, so keep it to yourself.",
  ignore: "Didn't ask for this? Ignore this email and we won't send you anything else.",
  signoff: "The Sked Social team",
  footer:
    "You're getting this because someone asked for a prompt pack for this address at skedsocial.com/tools/content-prompt-builder.",
} as const;

/** Making a board on the pack page. */
export const BOARD_PICK_COPY = {
  actionsButton: "Make a board",
  shortlistHeading: "Share a shortlist",
  shortlistBody: "Pick 5 to 10 prompts and share them as a public board with your team or clients.",
  heading: "Make a board",
  sub: "Pick 5 to 10 prompts. Your board shows your brand name, audience and the prompts you pick.",
  pickLabel: "Add {title} to a board",
  counter: "{n} of 10 picked",
  tooFew: "Pick at least 5 prompts.",
  tooMany: "That's 10. Remove one to add another.",
  publish: "Publish board",
  privacy:
    "Boards are public. Anyone with the link can see your brand name, audience and the prompts you picked.",
  rateLimited: "You've published a lot of boards today. Try again tomorrow.",
  publishedHeading: "Your board is live",
  yourBoards: "Your boards",
  boardSummary: "{count} prompts, published {date}",
  copyLink: "Copy link",
  deleteButton: "Delete board",
  deleteConfirm: "Delete this board? The link will stop working.",
} as const;

/** The public board page, /b/:slug. */
export const BOARD_PAGE_COPY = {
  eyebrow: "Prompt board",
  h1: "{count} AI prompts from {brand}",
  sub: "Picked by {brand} for {platform}. Copy any prompt into ChatGPT, Gemini or your AI tool.",
  copy: "Copy",
  privacy:
    "This board is public. Anyone with the link can see it. It's deleted after 90 days without a visit.",
  ctaHeading: "Build prompts for your own brand",
  ctaBody: "Add your brand and audience and get a prompt you can use today. Free, no email needed.",
  ctaButton: "Build my prompt",
  footer: "Made with the free AI Content Prompt Builder from Sked Social.",
  expiredHeading: "This board has expired",
  expiredBody: "Boards are deleted after 90 days without a visit.",
  /** Also on the owner's pack page, beside each published board. */
  availableUntil: "Available until {date}",
  /** Public board page only. */
  staysUp:
    "Boards close 90 days after their last visit, or sooner if their owner's prompts expire.",
} as const;

export const BOARD_OG_COPY = {
  cardTitle: "{count} AI content prompts for {brand}",
  cardSubtitle: "Picked with the free prompt builder from Sked Social",
  ogTitle: "{count} AI content prompts for {brand} | Sked Social",
  ogDescription:
    "{count} ready-to-paste AI prompts for {platform}, picked by {brand}. Build your own, free.",
} as const;

/** "{brand}"-style substitution for the deck's tokens. Missing values stay as the token. */
export function fillCopy(text: string, values: Record<string, string>): string {
  return text.replace(/\{(\w+)\}/g, (token, key: string) => values[key] ?? token);
}
